import type { components } from '@joyful/openapi/types.gen'
import { serializeEncounterNavigationSource } from '../../encounters/hooks/useEncounterNavigationSource'
import { appendPhoneCallTaskNavigationSource } from '../../phone-call-tasks/hooks/usePhoneCallTaskNavigationSource'
import { getPhoneCallTaskHref } from '../../phone-call-tasks/components/PhoneCallTaskLink'
import { createEnumSchema, pluralize, UserRoleSchema } from '@joyful/utils'
export type ListWorkBlocksResponse =
  components['schemas']['ListWorkBlocksResponse']
export type WorkBlocksMetrics = components['schemas']['WorkBlocksMetrics']
export type WorkBlock = components['schemas']['WorkBlockWithEncounters']
export type WorkBlockAssignment = WorkBlock['assignments'][number]
export type WorkBlockEncounterAssignment =
  components['schemas']['WorkBlockAssignmentWithEncounter']
export type WorkBlockAssignmentIncompletion =
  | components['schemas']['AssignmentIncompletion']
  | components['schemas']['EncounterStateIncompletion']
  | components['schemas']['OpenJoyfulWorkIncompletion']
export type WorkBlockPhoneCallTaskAssignment =
  components['schemas']['WorkBlockAssignmentWithPhoneCallTask']
export type WorkBlockAssignmentGroup = NonNullable<
  WorkBlock['assignment_groups']
>[number]
export type EncounterListItem = components['schemas']['EncounterListItem']
export type Biller = components['schemas']['UserRead']
export type UnassignedBiller = components['schemas']['UnassignedBiller']

export interface WorkBlockAssignmentStats {
  completedAssignments: number
  totalAssignments: number
}

export function isReserveWorkBlock(block: WorkBlock): boolean {
  return block.kind === 'RESERVE'
}

export function formatWorkBlockSubjects({
  encounterCount,
  phoneCallTaskCount,
  selectedPrefix = false,
}: {
  encounterCount: number
  phoneCallTaskCount: number
  selectedPrefix?: boolean
}) {
  const encounterNoun = selectedPrefix ? 'selected encounter' : 'encounter'
  const callTaskNoun = selectedPrefix ? 'selected call task' : 'call task'
  return [
    encounterCount > 0 ? pluralize(encounterCount, encounterNoun) : null,
    phoneCallTaskCount > 0 ? pluralize(phoneCallTaskCount, callTaskNoun) : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

export function collectEncounterAssignments(block: WorkBlock) {
  return block.assignments.filter(
    (assignment): assignment is WorkBlockEncounterAssignment => {
      return assignment.subject_type === 'ENCOUNTER'
    },
  )
}

export function collectPhoneCallTaskAssignments(block: WorkBlock) {
  return block.assignments.filter(
    (assignment): assignment is WorkBlockPhoneCallTaskAssignment => {
      return assignment.subject_type === 'PHONE_CALL_TASK'
    },
  )
}

export function collectBlockEncounters(
  block: WorkBlock,
): Array<EncounterListItem> {
  return collectEncounterAssignments(block).map((assignment) => {
    return assignment.encounter
  })
}

export interface WorkBlockEncounters {
  allEncounters: Array<EncounterListItem>
  assignmentsByEncounterId: Map<string, WorkBlockEncounterAssignment>
  completedEncounterIds: Set<string>
  completedEncounters: Array<EncounterListItem>
  todoEncounters: Array<EncounterListItem>
}

export function collectOrderedBlockEncounters(
  block: WorkBlock,
): WorkBlockEncounters {
  return collectOrderedAssignmentEncounters(collectEncounterAssignments(block))
}

export function collectOrderedPhoneCallTaskAssignments(
  assignments: Array<WorkBlockPhoneCallTaskAssignment>,
) {
  const todoAssignments = new Array<WorkBlockPhoneCallTaskAssignment>()
  const completedAssignments = new Array<WorkBlockPhoneCallTaskAssignment>()

  for (const assignment of assignments) {
    if (assignment.completed_at) {
      completedAssignments.push(assignment)
    } else {
      todoAssignments.push(assignment)
    }
  }

  completedAssignments.sort((a, b) => {
    const aCompletedAt = a.completed_at ?? ''
    const bCompletedAt = b.completed_at ?? ''
    return bCompletedAt.localeCompare(aCompletedAt)
  })

  return todoAssignments.concat(completedAssignments)
}

export function collectPhoneCallTaskAssignmentsForDisplay({
  block,
  canShow,
}: {
  block: WorkBlock
  canShow: boolean
}) {
  if (!canShow) {
    return []
  }

  return collectOrderedPhoneCallTaskAssignments(
    collectPhoneCallTaskAssignments(block),
  )
}

export function collectOrderedAssignmentEncounters(
  assignments: Array<WorkBlockEncounterAssignment>,
): WorkBlockEncounters {
  const assignmentsByEncounterId = new Map<
    string,
    WorkBlockEncounterAssignment
  >()
  const completedEncounterIds = new Set<string>()
  const todoEncounters = new Array<components['schemas']['EncounterListItem']>()
  const completedAssignments = new Array<WorkBlockEncounterAssignment>()

  for (const assignment of assignments) {
    assignmentsByEncounterId.set(assignment.encounter_id, assignment)
    if (assignment.completed_at) {
      completedEncounterIds.add(assignment.encounter_id)
      completedAssignments.push(assignment)
    } else {
      todoEncounters.push(assignment.encounter)
    }
  }

  completedAssignments.sort((a, b) => {
    const aCompletedAt = a.completed_at ?? ''
    const bCompletedAt = b.completed_at ?? ''
    return bCompletedAt.localeCompare(aCompletedAt)
  })

  const completedEncounters = completedAssignments.map((assignment) => {
    return assignment.encounter
  })

  return {
    allEncounters: todoEncounters.concat(completedEncounters),
    assignmentsByEncounterId,
    completedEncounterIds,
    completedEncounters,
    todoEncounters,
  }
}

export function getAssignmentStats(
  block: WorkBlock | null | undefined,
): WorkBlockAssignmentStats {
  if (!block) {
    return {
      completedAssignments: 0,
      totalAssignments: 0,
    }
  }

  return {
    completedAssignments: block.completed_assignment_count,
    totalAssignments: block.assignment_count,
  }
}

export const WorkBlockAssigneeRoleSchema = createEnumSchema([
  {
    title: UserRoleSchema.meta('BILLER').title,
    value: 'BILLER',
  },
  {
    title: UserRoleSchema.meta('BILLING_SUPERVISOR').title,
    value: 'BILLING_SUPERVISOR',
  },
  {
    title: UserRoleSchema.meta('RCM_SPECIALIST').title,
    value: 'RCM_SPECIALIST',
  },
  {
    title: UserRoleSchema.meta('BILLING_MANAGER').title,
    value: 'BILLING_MANAGER',
  },
])

export const WorkBlockAssignmentCompletionSchema = createEnumSchema([
  {
    description: '0 assigned',
    title: 'Unassigned',
    value: 'unassigned',
  },
  {
    description: '> 0 assigned but 0 worked',
    title: 'Not started',
    value: 'not_started',
  },
  {
    description: '> 0 worked but not complete',
    title: 'In progress',
    value: 'in_progress',
  },
  {
    description: 'Worked ≥ assigned',
    title: 'Complete',
    value: 'complete',
  },
])

export function getWorkBlockAssignmentCompletion(
  stats: WorkBlockAssignmentStats,
) {
  if (stats.totalAssignments === 0) {
    return 'unassigned'
  }

  if (stats.completedAssignments === 0) {
    return 'not_started'
  }

  if (stats.completedAssignments >= stats.totalAssignments) {
    return 'complete'
  }

  return 'in_progress'
}

export const BillerAssignmentStatusSchema = createEnumSchema<
  components['schemas']['BillerAssignmentStatus']
>([
  { title: `On`, value: 'ON' },
  { title: `Paused`, value: 'PAUSED' },
  { title: `Off`, value: 'OFF' },
])

export interface BuildBlockEncounterUrlParams {
  blockBillerUserId: string | null | undefined
  currentUserId: string
  encounterId: string
  orgSlug: string
}

export function buildBlockEncounterUrl({
  blockBillerUserId,
  currentUserId,
  encounterId,
  orgSlug,
}: BuildBlockEncounterUrlParams) {
  if (!blockBillerUserId) {
    return `/${orgSlug}/${encounterId}`
  }

  const isOwnBlock = blockBillerUserId === currentUserId
  return serializeEncounterNavigationSource(
    `/${orgSlug}/${encounterId}`,
    isOwnBlock
      ? { source: 'block' }
      : { entityId: blockBillerUserId, source: 'block' },
  )
}

export interface BuildBlockPhoneCallTaskUrlParams {
  blockBillerUserId: string | null | undefined
  currentUserId: string
  orgSlug: string
  phoneCallTaskId: string
}

export function buildBlockPhoneCallTaskUrl({
  blockBillerUserId,
  currentUserId,
  orgSlug,
  phoneCallTaskId,
}: BuildBlockPhoneCallTaskUrlParams) {
  const href = getPhoneCallTaskHref({ orgSlug, phoneCallTaskId })
  if (!blockBillerUserId) {
    return href
  }

  const isOwnBlock = blockBillerUserId === currentUserId
  return appendPhoneCallTaskNavigationSource(
    href,
    isOwnBlock
      ? { source: 'block' }
      : { entityId: blockBillerUserId, source: 'block' },
  )
}
